/* /bibleproject — Free BibleProject video library, embedded on Faitharama.
   All videos are free, donor-supported by BibleProject. Faitharama charges nothing. */
import { useState } from "react";
import { Link } from "react-router-dom";
import SiteNav from "../components/SiteNav";
import { Play, Check, Share2, Heart } from "lucide-react";

const GREEN  = "#2E9F3A";
const ORANGE = "#F39A1F";
const BRAND  = "#287F8E";
const WHITE  = "#FFFFFF";

/* Curated rows — every video below is a confirmed real BibleProject YouTube ID.
   Verified Feb 2026 from web research against bibleproject.com & their YouTube channel. */
const ROWS = [
  {
    label: "★ START HERE — Watch This First",
    accent: ORANGE,
    videos: [
      { id: "ak06MSETeo4", title: "What Is the Bible?" },
      { id: "7_CGP-12AE0", title: "The Story of the Bible" },
    ],
  },
  {
    label: "★ Old Testament — Book by Book",
    accent: BRAND,
    videos: [
      { id: "GQI72THyO5I", title: "Genesis 1–11" },
      { id: "F4isSyennFo", title: "Genesis 12–50" },
      { id: "jH_aojNJM3E", title: "Exodus 1–18" },
      { id: "oNpTha80yyE", title: "Exodus 19–40" },
      { id: "IJ-FekWUZzE", title: "Leviticus" },
      { id: "tp5MIrMZFqo", title: "Numbers" },
      { id: "q5QEH9bH8AU", title: "Deuteronomy" },
      { id: "j9phNEaPrv8", title: "Psalms" },
      { id: "AzmYV8GNAIM", title: "Proverbs" },
    ],
  },
  {
    label: "★ New Testament — Book by Book",
    accent: GREEN,
    videos: [
      { id: "3Dv4-n6OYGI", title: "Matthew 1–13" },
      { id: "OVRixfameGY", title: "Mark" },
      { id: "26z_KhwNdD8", title: "Luke 10–24" },
      { id: "G-2e9mMf7E8", title: "John 1–12" },
      { id: "ej_6dVdJSIU", title: "Romans 1–4" },
      { id: "5nvVVcYD-0w", title: "Revelation 1–11" },
    ],
  },
];

export default function BibleProjectPage() {
  const [watched, setWatched] = useState(() => {
    try { return JSON.parse(localStorage.getItem("bp_watched") || "[]"); }
    catch { return []; }
  });

  const openVideo = (id) => {
    window.open(`https://www.youtube.com/watch?v=${id}`, "_blank", "noopener,noreferrer");
    const next = Array.from(new Set([...watched, id]));
    setWatched(next);
    try { localStorage.setItem("bp_watched", JSON.stringify(next)); } catch { /* ignore */ }
  };

  const share = () => {
    const url = window.location.origin + "/bibleproject";
    navigator.clipboard.writeText(url);
    alert("Link copied — share it anywhere.");
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F3EE" }} data-testid="bibleproject-page">
      <SiteNav />

      {/* TINY FAITHARAMA-ONLY DONATE STRIP — kept separate from BibleProject so no one accidentally clicks the wrong one */}
      <div className="px-4 py-2 text-right border-b" style={{ background: "#FFFBEF", borderColor: `${ORANGE}33` }}>
        <a href="/donate"
           data-testid="fa-donate-top-small"
           className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] tracking-[0.18em] uppercase font-bold text-white"
           style={{ background: ORANGE }}>
          <Heart className="w-3 h-3" fill="white"/> Support Faitharama
        </a>
      </div>

      {/* HERO */}
      <header className="px-4 md:px-8 py-10 md:py-14" style={{ background: "#EAF6FC", borderBottom: `4px solid ${ORANGE}` }}>
        <div className="max-w-6xl mx-auto">
          {/* TWO LOGOS — FAITHARAMA BRINGS YOU BIBLEPROJECT */}
          <div className="flex flex-wrap items-center justify-center gap-4 md:gap-6 mb-6">
            <img src="/logos/faitharama.png" alt="Faitharama.com" className="h-16 md:h-20 w-auto object-contain"/>
            <span className="font-serif-display text-base md:text-xl uppercase tracking-[0.18em]" style={{ color: "#0F172A", fontWeight: 700 }}>
              brings you
            </span>
            <img src="/logos/bibleproject.png" alt="BibleProject" className="h-16 md:h-20 w-auto object-contain rounded"/>
          </div>
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-2 text-center" style={{ color: GREEN }}>
            ★ 100% FREE — FOREVER. NO CATCH.
          </div>
          <h1 className="font-serif-display text-2xl md:text-4xl uppercase leading-[1.1] text-center" style={{ color: "#0F172A", fontWeight: 800 }}>
            For Free.
          </h1>
          <p className="text-base md:text-lg text-foreground/85 mt-4 max-w-3xl mx-auto text-center leading-relaxed">
            We are <strong>so proud</strong> of the work these guys do, we wanted to share them with everyone.
            <br className="hidden md:block"/>
            <strong style={{ color: GREEN }}>Affiliates — please take advantage of this free service.</strong>
            {" "}Help people learn about Jesus in a cool way by the best in the business.
          </p>
          <div className="mt-5 flex flex-wrap gap-2 justify-center">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: GREEN, border: `2px solid ${GREEN}` }}>
              <Check className="w-3.5 h-3.5"/> FREE FOREVER
            </span>
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white text-[11px] tracking-[0.18em] uppercase font-bold" style={{ color: BRAND, border: `2px solid ${BRAND}` }}>
              200+ Videos
            </span>
            <button onClick={share} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] tracking-[0.18em] uppercase font-bold text-white"
                    style={{ background: ORANGE }} data-testid="bp-share">
              <Share2 className="w-3.5 h-3.5"/> Share This Page
            </button>
          </div>

          {/* ─────────── BIBLEPROJECT DONATE — TOP OF PAGE (main button) ─────────── */}
          <div className="mt-7 text-center" data-testid="bp-donate-top">
            <a href="https://bibleproject.com/give/" target="_blank" rel="noopener noreferrer"
               data-testid="bp-donate-top-open"
               className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-bold uppercase tracking-[0.18em] text-white shadow hover:scale-[1.03] transition"
               style={{ background: GREEN }}>
              <Heart className="w-4 h-4" fill="white"/> Donate to BibleProject
            </a>
            <div className="mt-2 text-[10px] tracking-[0.18em] uppercase font-bold opacity-70" style={{ color: "#0F172A" }}>
              100% goes straight to BibleProject — Faitharama keeps nothing
            </div>
          </div>
        </div>
      </header>

      {/* ROWS */}
      <div className="px-4 md:px-8 py-8 max-w-6xl mx-auto space-y-9">
        {ROWS.map((row) => (
          <section key={row.label} data-testid={`bp-row-${row.label.replace(/\W+/g,"-")}`}>
            <div className="flex items-baseline justify-between gap-3 mb-3">
              <h2 className="font-serif-display text-xl md:text-2xl uppercase leading-tight" style={{ color: row.accent, fontWeight: 800 }}>
                {row.label}
              </h2>
              <span className="text-[10px] tracking-[0.22em] uppercase font-bold" style={{ color: GREEN }}>
                ★ FREE
              </span>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
              {row.videos.map((v) => (
                <button key={v.id} onClick={() => openVideo(v.id)}
                        data-testid={`bp-video-${v.id}`}
                        className="relative shrink-0 w-60 md:w-72 rounded-lg overflow-hidden border-2 bg-white shadow hover:scale-[1.03] transition cursor-pointer text-left"
                        style={{ borderColor: `${row.accent}55` }}>
                  <div className="relative" style={{ aspectRatio: "16/9", background: "#000" }}>
                    <img src={`https://i.ytimg.com/vi/${v.id}/hqdefault.jpg`}
                         alt={v.title}
                         className="w-full h-full object-cover" loading="lazy"
                         onError={(e) => { e.target.style.display = "none"; }}/>
                    <div className="absolute inset-0 flex items-center justify-center bg-black/10">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center shadow-lg" style={{ background: ORANGE }}>
                        <Play className="w-5 h-5 text-white" fill="white"/>
                      </div>
                    </div>
                    <span className="absolute top-1.5 right-1.5 px-2 py-0.5 rounded-full text-[9px] tracking-[0.22em] uppercase font-bold text-white shadow"
                          style={{ background: GREEN }}>★ FREE</span>
                    {watched.includes(v.id) && (
                      <span className="absolute bottom-1.5 left-1.5 px-1.5 py-0.5 rounded text-[9px] tracking-[0.18em] uppercase font-bold bg-black/70 text-white">
                        WATCHED
                      </span>
                    )}
                  </div>
                  <div className="px-3 py-2">
                    <div className="text-sm font-bold leading-tight" style={{ color: "#0F172A" }}>{v.title}</div>
                    <div className="text-[10px] tracking-[0.18em] uppercase font-bold mt-0.5" style={{ color: GREEN }}>
                      ★ Watch on YouTube →
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>

      {/* FOOTER */}
      <footer className="px-4 md:px-8 py-8 mt-4 border-t-2 text-center" style={{ background: WHITE, borderColor: `${ORANGE}55` }}>
        <p className="text-sm text-foreground/80 max-w-3xl mx-auto inline-flex items-center justify-center flex-wrap gap-1.5">
          All videos are produced by <a href="https://bibleproject.com" target="_blank" rel="noopener noreferrer" className="underline font-bold" style={{ color: BRAND }}>BibleProject</a>,
          a donor-supported 501(c)(3) nonprofit. <strong>This is a free service we give.</strong>
          <img src="/logos/faitharama.png" alt="Faitharama" className="inline-block h-5 w-auto mx-1 align-middle"/>
          charges nothing for any of this content. Free to watch. Free to share. Free to use in your church or small group.
        </p>
        <div className="mt-5 mx-auto max-w-2xl rounded-xl border-2 p-4 md:p-5 text-center" style={{ background: "#EAF6FC", borderColor: GREEN }}>
          <div className="text-[11px] tracking-[0.32em] uppercase font-bold mb-1" style={{ color: GREEN }}>★ A WORD ON GIVING</div>
          <p className="font-serif-display text-base md:text-lg leading-snug" style={{ color: "#0F172A" }}>
            If you would like to donate, it goes straight to BibleProject. <em>We pray you give what you are led to give.</em>
          </p>
          <a href="https://bibleproject.com/give/" target="_blank" rel="noopener noreferrer"
             data-testid="bp-donate-open"
             className="mt-3 inline-flex items-center justify-center gap-1.5 px-5 py-2.5 rounded-md text-xs uppercase tracking-[0.18em] font-bold text-white shadow"
             style={{ background: GREEN }}>
            <Heart className="w-3.5 h-3.5" fill="white"/> Donate to BibleProject
          </a>
        </div>
        <div className="mt-4 flex items-center justify-center gap-2">
          <Link to="/" className="inline-flex items-center gap-2">
            <span className="text-[11px] tracking-[0.28em] uppercase font-bold" style={{ color: BRAND }}>★ Back to</span>
            <img src="/logos/faitharama.png" alt="Faitharama" className="h-6 w-auto"/>
          </Link>
        </div>
      </footer>
    </div>
  );
}
